export const termsAndConditionsContent = `# Sinar Mas Digital Awards 2026
## Terms and Conditions

---

### **Preamble**

The **Sinar Mas Digital Awards 2026** (“Award”) is organized by the **Sinar Mas Digital** team as part of the company’s initiative to encourage innovation, collaboration, and digital transformation across the Sinar Mas ecosystem.

These Terms and Conditions (“Terms”) govern participation in the Award submission, evaluation, and recognition process. **By submitting an entry, Participants agree to comply with these Terms.**

---

### **Article 1 – Definitions**

*   **1.1 Award** refers to the Sinar Mas Digital Awards 2026 program organized by Sinar Mas Digital.
*   **1.2 Participant** refers to any individual, team, department, business unit, affiliate, or representative submitting an entry to the Award.
*   **1.3 Submission** refers to all materials, documents, forms, presentations, videos, or supporting evidence submitted by a Participant.
*   **1.4 Organizer** refers to Sinar Mas Digital and its authorized representatives, committees, partners, and appointed judges.
*   **1.5 Platform** refers to the digital portal or system used for Award registration, submission, judging, communication, and event-related activities.

---

### **Article 2 – Eligibility**

*   **2.1** The Award is open to employees, teams, departments, business units, subsidiaries, and affiliated entities within the Sinar Mas ecosystem, unless otherwise specified by the Organizer.
*   **2.2** Participants may submit entries individually or as a group/team submission.
*   **2.3** Participants may submit multiple entries across different categories, provided that each submission represents a distinct initiative, project, achievement, or use case.
*   **2.4** The Top 3 winners from the previous year in the same category are not eligible to submit entries to that category in the current Award cycle.
*   **2.5** Individual award categories, including Women in Tech and Digital Catalyst, are open to eligible Participants without any age restriction.
*   **2.6** All submissions must be submitted within the official submission period announced by the Organizer.
*   **2.7** The Organizer reserves the right to determine and verify Participant eligibility.

---

### **Article 3 – Submission Requirements**

*   **3.1** All submissions must contain complete, accurate, and truthful information.
*   **3.2** Participants are responsible for ensuring that all submitted materials:
    *   Are original or properly authorized;
    *   Do not infringe any third-party rights;
    *   Comply with applicable company policies and laws.
*   **3.3** Participants may be required to provide supporting documents, presentations, images, videos, metrics, references, approvals, or additional clarification during the evaluation process.
*   **3.4** Submissions may be prepared in **Bahasa Indonesia** or **English** unless otherwise stated.
*   **3.5** The Organizer reserves the right to reject incomplete, misleading, inappropriate, or late submissions.

---

### **Article 4 – Intellectual Property Rights**

*   **4.1** Participants retain ownership of their intellectual property rights related to their submissions.
*   **4.2** By submitting an entry, Participants grant the Organizer a **non-exclusive, worldwide, royalty-free license** to:
    *   Review and evaluate the submission;
    *   Use submission materials for Award administration;
    *   Display submission materials during Award-related activities;
    *   Use selected materials for internal communication, documentation, promotional, educational, or knowledge-sharing purposes.
*   **4.3** The Organizer may publish, adapt, summarize, or showcase selected submissions, including winning or shortlisted entries, in internal or external materials, provided that:
    *   Sensitive or confidential information is removed or anonymized where appropriate;
    *   Publication complies with applicable company policies;
    *   The publication is intended for educational, promotional, or knowledge-sharing purposes.
*   **4.4** Participants represent and warrant that they have the necessary authority and permissions to submit all materials included in their submission.

---

### **Article 5 – Evaluation and Judging**

*   **5.1** Submissions will be evaluated based on criteria determined by the Organizer for each category.
*   **5.2** Evaluations may include administrative review, scoring, presentations, interviews, demonstrations, validation sessions, or additional assessment stages.
*   **5.3** The Organizer may appoint internal and/or external judges, evaluators, advisors, or subject matter experts.
*   **5.4** **All evaluation results and judging decisions are final and binding.**
*   **5.5** The Organizer reserves the right to:
    *   Disqualify any submission that violates these Terms;
    *   Request clarification or additional information;
    *   Merge, modify, or cancel categories;
    *   Withhold awards if deemed necessary.

---

### **Article 6 – Awards and Recognition**

*   **6.1** Winners, finalists, or shortlisted Participants may receive trophies, certificates, recognition, monetary prizes, or other forms of appreciation as determined by the Organizer.
*   **6.2** The Organizer reserves the right to modify the prize structure, award categories, or recognition format at any time.
*   **6.3** Participants are solely responsible for any applicable tax obligations arising from prizes or rewards received.
*   **6.4** Winners may be requested to participate in documentation, interviews, promotional activities, presentations, or publicity related to the Award.

---

### **Article 7 – Privacy and Data Protection**

*   **7.1** The Organizer may collect, process, store, and use personal data and submission-related information for purposes including:
    *   Award administration & communication;
    *   Evaluation and judging;
    *   Event operations & analytics;
    *   Publication and internal knowledge-sharing.
*   **7.2** Personal data will be processed in accordance with applicable laws and internal policies, including relevant **Indonesian data protection regulations**.
*   **7.3** Participants confirm that any personal data submitted has been obtained lawfully and with appropriate authorization.
*   **7.4** The Organizer may retain submission and participation data for as long as reasonably necessary to fulfill operational, legal, audit, or business purposes.

---

### **Article 8 – Confidentiality**

*   **8.1** Participants may identify certain information within submissions as confidential.
*   **8.2** The Organizer will take reasonable measures to limit unauthorized disclosure of confidential information.
*   **8.3** Participants are responsible for ensuring that submitted materials are appropriate for sharing within the Award evaluation process.
*   **8.4** The Organizer reserves the right to remove, anonymize, or exclude sensitive information before using materials for publication.

---

### **Article 9 – Liability and Indemnification**

*   **9.1** The Organizer shall not be liable for technical issues, submission failures, system interruptions, unauthorized access, data loss, or circumstances beyond reasonable control.
*   **9.2** Participants agree to **indemnify and hold harmless** the Organizer from claims, damages, or disputes arising from their submissions, infringement of third-party rights, or violations of laws/policies.
*   **9.3** The Organizer is not responsible for any indirect, incidental, or consequential damages related to participation.

---

### **Article 10 – Modifications and Cancellation**

*   **10.1** The Organizer reserves the right to amend, update, or modify these Terms at any time.
*   **10.2** The Organizer may modify, postpone, suspend, or cancel any part of the Award, including timelines and events.
*   **10.3** Any material updates will be communicated through official channels or the Award Platform.

---

### **Article 11 – Publication and Knowledge Sharing**

*   **11.1** Selected submissions may be featured as case studies, showcase materials, presentations, or articles.
*   **11.2** Any published materials may be edited, summarized, or adapted for clarity and confidentiality.
*   **11.3** The Award aims to promote collaboration, innovation, and knowledge-sharing across the Sinar Mas ecosystem.

---

### **Article 12 – Governing Law**

*   **12.1** These Terms shall be governed by and interpreted in accordance with the laws of the **Republic of Indonesia**.
*   **12.2** Any disputes shall be subject to the jurisdiction of the applicable courts in Indonesia.

---

### **Article 13 – Contact and Support**

Questions regarding the Award or these Terms may be directed to the official **Sinar Mas Digital Awards organizing committee**.

---

### **Article 14 – Acceptance**

*   **14.1** By submitting an entry, Participants acknowledge they have read, understood, and agree to these Terms.
*   **14.2** Participants agree to comply with all applicable laws, regulations, and ethical standards.

---

**Effective Date:** 1 May 2026  
**Last Updated:** May 2026  
**Version:** 2.0`;
